/**
 * Triage admin de cobros PAC fallidos. Para cada cuota permite reintentar PAC en
 * el próximo batch BCI, bascular la cobranza desde PAC hacia Lups, o abandonar
 * el cobro definitivamente (la gestión del pagaré es manual fuera del sistema).
 */

import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface FailedItem {
  id: string;
  companyId: string;
  companyName: string | null;
  customerCode: string | null;
  monto: string;
  dueAt: string | null;
  pacLastFailureCode: string | null;
  rejectionDescription: string | null;
  rejectionCcaCode: string | null;
  pacRetryCount: number;
  lastPacAttemptAt: string | null;
  pacActive: boolean;
}

interface FailedList {
  data: FailedItem[];
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE = 20;

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtMoney = (raw: string) => {
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
};

export default function PacFallidosPage() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<FailedList | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      const url = `${getAdminEndpointUrl('pacFailures', 'failed')}?${params.toString()}`;
      const result = await adminHttpClient.get<FailedList>(url);
      setData(result);
    } catch (err) {
      console.error('Error cargando fallidos:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const runAction = async (action: 'retryPac' | 'forceLups' | 'abandon', item: FailedItem, confirmMsg: string) => {
    if (!confirm(confirmMsg)) return;
    setActionInProgress(item.id);
    setError(null);
    try {
      const url = getAdminEndpointUrl('pacFailures', action).replace('{id}', item.id);
      await adminHttpClient.post(url, {});
      void fetchList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error ejecutando acción');
    } finally {
      setActionInProgress(null);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Cobros PAC fallidos</h1>
          <p className="mt-1 text-sm text-slate-500">
            Cuotas con cobro PAC rechazado por BCI. Decide si reintentar PAC en el próximo batch, forzar cobro Lups one-shot, o abandonar (gestión de pagaré es externa).
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
              <tr>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Cuota</th>
                <th className="px-4 py-3">Vencimiento</th>
                <th className="px-4 py-3">Motivo del rechazo</th>
                <th className="px-4 py-3">Reintentos</th>
                <th className="px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && !data ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Cargando…
                  </td>
                </tr>
              ) : !data || data.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Sin cobros fallidos.
                  </td>
                </tr>
              ) : (
                data.data.map((it) => {
                  const inProgress = actionInProgress === it.id;
                  return (
                    <tr key={it.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{it.companyName ?? '—'}</p>
                        <p className="font-mono text-[11px] text-slate-500">{it.companyId}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium">{fmtMoney(it.monto)}</p>
                        <p className="font-mono text-[11px] text-slate-500">cliente {it.customerCode ?? '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-xs">{fmtDate(it.dueAt)}</td>
                      <td className="px-4 py-3">
                        <p className="font-mono text-xs">
                          {it.pacLastFailureCode ?? '—'}
                          {it.rejectionCcaCode ? <span className="ml-1 text-[10px] text-slate-500">/{it.rejectionCcaCode}</span> : null}
                        </p>
                        <p className="text-[11px] text-slate-600 dark:text-slate-400">{it.rejectionDescription ?? '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <p>{it.pacRetryCount}</p>
                        <p className="text-[11px] text-slate-500">último: {fmtDateTime(it.lastPacAttemptAt)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={inProgress}
                            onClick={() => void runAction('retryPac', it, '¿Reintentar PAC en el próximo batch?')}
                            className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-300"
                          >
                            Reintentar PAC
                          </button>
                          <button
                            type="button"
                            disabled={inProgress}
                            onClick={() => void runAction('forceLups', it, '¿Forzar cobro Lups one-shot? (endpoint Lups one-shot pendiente — re-encolará el envío Lups)')}
                            className="rounded border border-purple-200 bg-purple-50 px-2 py-1 text-xs text-purple-700 hover:bg-purple-100 disabled:opacity-50 dark:border-purple-900 dark:bg-purple-900/20 dark:text-purple-300"
                          >
                            Forzar Lups
                          </button>
                          <button
                            type="button"
                            disabled={inProgress}
                            onClick={() => void runAction('abandon', it, '⚠️ Abandonar la cuota es definitivo. La gestión del pagaré es manual fuera del sistema. ¿Continuar?')}
                            className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
                          >
                            Abandonar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {data && data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs dark:border-slate-800">
              <span className="text-slate-500">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700">
                  Anterior
                </button>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50 dark:border-slate-700">
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
